import React from 'react';

import { FormattedMessage } from '../../../util/reactIntl';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { formatMoney } from '../../../util/currency';
import { registrationFeeCoverageItems } from '../../../util/listingData';
import { getDefaultSearchOrigin } from '../../../util/search';
import { distanceInMiles, formatDistanceMiles } from '../../../util/distance';

import { sessionDurationMinutes } from './classDetailData';

import css from './ClassDetail.module.css';
import { showsAsTerm, termWeeks } from '../../../util/termSchedule';

const { Money } = sdkTypes;

const IconClock = () => (
  <svg className={css.quickInfoIcon} viewBox="0 0 24 24" role="none">
    <circle cx="12" cy="12" r="9" fill="none" strokeWidth="2" />
    <path d="M12 7v5l3 2" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconPin = () => (
  <svg className={css.quickInfoIcon} viewBox="0 0 24 24" role="none">
    <path
      d="M12 21s-7-5.1-7-11a7 7 0 1 1 14 0c0 5.9-7 11-7 11z"
      fill="none"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.5" fill="none" strokeWidth="2" />
  </svg>
);

const IconDollar = () => (
  <svg className={css.quickInfoIcon} viewBox="0 0 24 24" role="none">
    <path
      d="M12 3v18M16.5 7.5c-.9-1.2-2.6-2-4.5-2-2.5 0-4.5 1.3-4.5 3.2 0 4.3 9 2.3 9 6.6 0 1.9-2 3.2-4.5 3.2-1.9 0-3.6-.8-4.5-2"
      fill="none"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const IconAge = () => (
  <svg className={css.quickInfoIcon} viewBox="0 0 24 24" role="none">
    <circle cx="12" cy="8" r="4" fill="none" strokeWidth="2" />
    <path d="M5 21c0-3.9 3.1-7 7-7s7 3.1 7 7" fill="none" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const InfoItem = ({ icon, labelId, value }) =>
  value == null ? null : (
    <div className={css.quickInfoItem}>
      {icon}
      <div>
        <span className={css.quickInfoLabel}>
          <FormattedMessage id={labelId} />
        </span>
        <span className={css.quickInfoValue}>{value}</span>
      </div>
    </div>
  );

/**
 * The "Quick info" card: age range, session duration, location (address city + distance from the
 * parent's saved onboarding location), and the price with its per-child meaning. Items with no data
 * simply don't render -- nothing is faked.
 */
const ClassQuickInfo = props => {
  const { listing, currentUser, intl } = props;
  const { price, publicData = {}, geolocation } = listing.attributes || {};
  const { ageBandMin, ageBandMax } = publicData;
  // A one-session term (the old one-off workaround) is priced like a drop-in.
  const isTerm = showsAsTerm(publicData);

  const ageValue =
    Number.isInteger(ageBandMin) && Number.isInteger(ageBandMax)
      ? intl.formatMessage(
          { id: 'ClassDetailPage.ageRangeValue' },
          { minAge: ageBandMin, maxAge: ageBandMax }
        )
      : null;

  const duration = sessionDurationMinutes(listing);
  const durationValue =
    duration != null
      ? intl.formatMessage({ id: 'ClassDetailPage.durationValue' }, { minutes: duration })
      : null;

  const address = publicData.location?.address || null;
  const origin = getDefaultSearchOrigin(currentUser);
  const distanceLabel = formatDistanceMiles(distanceInMiles(origin, geolocation));
  const locationValue = address
    ? distanceLabel
      ? intl.formatMessage(
          { id: 'ClassDetailPage.locationWithDistance' },
          { address, miles: distanceLabel }
        )
      : address
    : null;

  const basePriceValue =
    price && price.amount != null
      ? isTerm && termWeeks(publicData)
        ? intl.formatMessage(
            { id: 'ClassDetailPage.priceValueTermWeeks' },
            { price: formatMoney(intl, price), weeks: termWeeks(publicData) }
          )
        : intl.formatMessage(
            {
              id: isTerm ? 'ClassDetailPage.priceValueTerm' : 'ClassDetailPage.priceValueSession',
            },
            { price: formatMoney(intl, price) }
          )
      : null;
  // Surface the one-time registration fee (publicData.registrationFee, subunits) BEFORE checkout.
  const registrationFee =
    Number.isInteger(publicData.registrationFee) && publicData.registrationFee > 0 && price?.currency
      ? new Money(publicData.registrationFee, price.currency)
      : null;
  // "Registration fee covers: …" -- the provider's picks (+ custom items) from the Pricing tab.
  const feeCoverageItems = registrationFee ? registrationFeeCoverageItems(publicData, intl) : [];
  const priceValue =
    basePriceValue && registrationFee
      ? intl.formatMessage(
          { id: 'ClassDetailPage.priceWithRegistrationFee' },
          { price: basePriceValue, fee: formatMoney(intl, registrationFee) }
        )
      : basePriceValue;

  if (!ageValue && !durationValue && !locationValue && !priceValue) {
    return null;
  }

  return (
    <section className={css.card}>
      <h2 className={css.sectionTitle}>
        <FormattedMessage id="ClassDetailPage.quickInfoTitle" />
      </h2>
      <div className={css.quickInfoGrid}>
        <InfoItem icon={<IconAge />} labelId="ClassDetailPage.ageRangeLabel" value={ageValue} />
        <InfoItem icon={<IconPin />} labelId="ClassDetailPage.locationLabel" value={locationValue} />
        <InfoItem
          icon={<IconClock />}
          labelId="ClassDetailPage.durationLabel"
          value={durationValue}
        />
        <InfoItem icon={<IconDollar />} labelId="ClassDetailPage.priceLabel" value={priceValue} />
      </div>
      {feeCoverageItems.length > 0 ? (
        <p className={css.feeCoverageNote}>
          <FormattedMessage
            id="ClassDetailPage.registrationFeeCovers"
            values={{ items: feeCoverageItems.join(', ') }}
          />
        </p>
      ) : null}
    </section>
  );
};

export default ClassQuickInfo;
